import React, { useState, useEffect, Suspense } from 'react';
import ToolTabToggleBtn from '../tool-tab/ToolTabToggleBtn.js';
import { useToolTab } from '../../contexts/ToolTabContext.js';
import useSidePanelWidgets from '../../hooks/useSidePanelWidgets.js';

function WidgetSkeleton() {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div className="skeleton-pulse" style={{ height: 14, borderRadius: 6, width: '60%' }} />
            <div className="skeleton-pulse" style={{ height: 14, borderRadius: 6, width: '85%' }} />
            <div className="skeleton-pulse" style={{ height: 14, borderRadius: 6, width: '70%' }} />
        </div>
    );
}

function WidgetContainer() {
    // 툴탭 상태 (상호 배타성 처리)
    const { isOpen: isToolTabOpen, setOpen: setToolTabOpen } = useToolTab();

    // 사이드 패널 위젯 구성 (표시 여부 / 순서)
    const { orderedWidgets, enabled, toggleWidget: toggleWidgetVisibility, moveWidget } = useSidePanelWidgets();
    const [editMode, setEditMode] = useState(false);

    // 화면 크기 감지
    const [isWideScreen, setIsWideScreen] = useState(window.innerWidth >= 1700);

    // 위젯 열림 상태 (localStorage에서 불러오기)
    const STORAGE_KEY_WIDGET_OPEN = 'astrantis_widget_open';
    const [isOpen, setIsOpen] = useState(() => {
        const saved = localStorage.getItem(STORAGE_KEY_WIDGET_OPEN);
        return saved ? saved === 'true' : false;
    });

    // 툴탭이 열리면 위젯 강제 닫기
    useEffect(() => {
        if (isToolTabOpen && isOpen) {
            setIsOpen(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isToolTabOpen]);

    // 로그인 여부 확인
    const isLoggedIn = localStorage.getItem('email') && localStorage.getItem('nickname');

    useEffect(() => {
        const handleResize = () => setIsWideScreen(window.innerWidth >= 1700);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 위젯 열림 상태 저장 + body 클래스 (ToolTab CSS 오프셋용)
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY_WIDGET_OPEN, isOpen.toString());
        document.body.classList.toggle('widget-open', isOpen);
    }, [isOpen]);

    // 토글 함수 — 열린 핸들을 다시 클릭하면 닫힌다(별도 닫기 핸들 없음)
    const toggleWidget = () => {
        const willOpen = !isOpen;
        // 위젯이 열릴 때 툴탭 닫기 (상호 배타성)
        if (willOpen && isToolTabOpen) {
            setToolTabOpen(false);
        }
        setIsOpen(willOpen);
    };

    if (!isLoggedIn) {
        return null;
    }

    // 위젯·툴탭 패널 중 하나라도 열려 있으면 핸들 스택을 패널(폭 300px) 좌측으로 이동
    const anyPanelOpen = isOpen || isToolTabOpen;

    return (
        <>
            <div className={`fab-stack ${anyPanelOpen ? 'panel-open' : ''}`}>
                {/* 위젯 토글 버튼 — 우측 상단 30% 지점에 고정, 화면 우측 엣지에 완전히 밀착 */}
                <button
                    className={`btn-fab btn-fab-primary widget-toggle-btn ${isOpen ? 'open' : 'closed'}`}
                    onClick={toggleWidget}
                    title={isOpen ? "Close Widget" : "Open Widget"}
                >
                    <span className="material-symbols-rounded btn-fab-icon-rest">more_vert</span>
                    <span className="material-symbols-rounded btn-fab-icon-active">
                        {isOpen ? 'close' : 'widgets'}
                    </span>
                </button>

                {/* 위젯 편집(세부 설정) 토글 버튼 — 위젯이 펼쳐졌을 때만 */}
                {isOpen && (
                    <button
                        className={`btn-fab btn-fab-neutral widget-edit-fab ${editMode ? 'active' : ''}`}
                        onClick={() => setEditMode(v => !v)}
                        title={editMode ? '편집 완료' : '위젯 편집'}
                    >
                        <span className="material-symbols-rounded btn-fab-icon-rest">more_vert</span>
                        <span className="material-symbols-rounded btn-fab-icon-active">{editMode ? 'check' : 'tune'}</span>
                    </button>
                )}

                {/* 툴탭(GIG) 토글 버튼 */}
                <ToolTabToggleBtn />
            </div>

            {/* 위젯 컨테이너 */}
            <div className={`widget-container ${isWideScreen ? 'sidebar-mode' : 'popup-mode'} ${isOpen ? 'open' : 'closed'}`}>
                <div className="widget-content">
                    {orderedWidgets.map((widget, index) => {
                        const isEnabled = enabled[widget.id];
                        if (!isEnabled && !editMode) return null;

                        const WidgetComponent = widget.component;
                        const showFullWidget = isEnabled && !editMode;
                        const itemClass = [
                            'widget-item',
                            widget.grow && showFullWidget ? 'widget-item-chat' : '',
                            !isEnabled ? 'widget-item-disabled' : '',
                        ].filter(Boolean).join(' ');

                        return (
                            <div key={widget.id} className={itemClass}>
                                {editMode && (
                                    <div className="widget-item-edit-bar">
                                        <span className="material-symbols-rounded widget-item-edit-icon">{widget.icon}</span>
                                        <span className="widget-item-edit-label">{widget.label}</span>
                                        <div className="widget-item-edit-actions">
                                            <button
                                                className="widget-item-edit-btn"
                                                onClick={() => moveWidget(widget.id, -1)}
                                                disabled={index === 0}
                                                title="위로 이동"
                                            >
                                                <span className="material-symbols-rounded">arrow_upward</span>
                                            </button>
                                            <button
                                                className="widget-item-edit-btn"
                                                onClick={() => moveWidget(widget.id, 1)}
                                                disabled={index === orderedWidgets.length - 1}
                                                title="아래로 이동"
                                            >
                                                <span className="material-symbols-rounded">arrow_downward</span>
                                            </button>
                                            <button
                                                className="widget-item-edit-btn"
                                                onClick={() => toggleWidgetVisibility(widget.id)}
                                                disabled={widget.removable === false}
                                                title={isEnabled ? '숨기기' : '표시하기'}
                                            >
                                                <span className="material-symbols-rounded">{isEnabled ? 'visibility' : 'visibility_off'}</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {editMode ? (
                                    <p className="widget-item-edit-desc">{widget.description}</p>
                                ) : (
                                    isEnabled && (
                                        <Suspense fallback={<WidgetSkeleton />}>
                                            <WidgetComponent />
                                        </Suspense>
                                    )
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
}

export default WidgetContainer;

